import { html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import './ix-file-chip.js';
import DataStorageUnit from './data-storage-unit.js';
import FileInfo from './internal/file-info.js';
import FileUploadResult from './internal/file-upload-result.js';
import FileChangeInfo from './internal/file-change-info.js';
import FilesUploadedInfo from './internal/files-uploaded-info.js';
import getTotalBytes from './internal/get-total-bytes.js';
import { processFiles } from './internal/process-files.js';
import sortFilesAscending from './internal/sort-files-ascending.js';
import IxFileUploaderStyles from './styles/ix-file-uploader-styles.js';
import UploaderFile from './internal/uploader-file.js';
import '@digital-realty/ix-dialog/ix-dialog.js';

export class IxFileUploader extends LitElement {
  static readonly styles = [IxFileUploaderStyles];

  /** @nocollapse  */
  static readonly formAssociated = true;

  private readonly internals = (this as HTMLElement).attachInternals();

  dropzoneRef: Ref<HTMLLabelElement> = createRef();

  fileRef: Ref<HTMLInputElement> = createRef();

  @property({ type: Boolean }) showTemplateFileLink: boolean = false;

  @property({ type: Boolean }) allowMultipleFiles: boolean = false;

  @property({ type: Boolean }) allowDuplicates: boolean = false;

  @property() headerText: string = '';

  @property() bodyText: string = '';

  @property() hintText: string | undefined = undefined;

  @property() ariaLabel: string = '';

  @property({ type: Array }) defaultFiles: Array<UploaderFile> = [];

  @property({ type: Boolean }) allowDeleteDefaultFiles: boolean = false;

  @property({ type: Boolean }) hideBody: boolean = false;

  @property({ type: Array }) extensions: string[] = [];

  @property({ type: Number }) maxFileCount: number = 10;

  @property() maxFileSizeUnit: DataStorageUnit = DataStorageUnit.MB;

  @property({ type: Number }) maxFileSizeValue: number = 10;

  @property() name: string = 'ix-file-uploader';

  @property({ type: Boolean }) error: boolean = false;

  @property({ type: Boolean }) askConfirmationOnDeleteFile: boolean = false;

  @property() deleteConfirmationDialogLabel: string =
    'Are you sure you want to delete your upload file?';

  @state() isDeleteBulkFileDialogOpen: boolean = false;

  @state() pendingFileDeletion: UploaderFile | undefined = undefined;

  @state() files: Array<UploaderFile> = [];

  @state() errors: Array<string> = [];

  /**
   * Alternating polite live regions used to announce non-error status
   * updates (successful uploads, removals). Two regions are toggled
   * between so that the DOM node hosting the message is fully replaced
   * on each announcement; this is more reliable across NVDA/JAWS than
   * mutating the text of a single node, especially for repeated
   * announcements with identical wording.
   */
  @state() private statusMessage: string = '';

  @state() private statusMessageAlt: string = '';

  @state() private statusRegionToggle: boolean = false;

  private statusAnnounceTimer: ReturnType<typeof setTimeout> | undefined;

  private maxFileSizeInBytes: number = 0;

  /**
   * The associated form element with which this element's value will submit.
   */
  get form() {
    return this.internals.form;
  }

  // called when the user drops files on the dropzone
  private onDrop(e: DragEvent): void {
    e.preventDefault();

    if (!e.dataTransfer) {
      return; // no files dropped
    }

    const filesToProcess = Array.from(e.dataTransfer.files);

    this.process(filesToProcess);
  }

  // This is called when the user selects files from the file dialog
  private onFileChange(e: Event): void {
    const fileList = (e.currentTarget as any).files as FileList;

    const filesToProcess = Array.from(fileList);

    this.process(filesToProcess);

    // clear the files from the file input; we are tracking state at the component level, not in the file input
    this.fileRef.value!.files = new DataTransfer().files;
  }

  get allowedMaxFileCount(): number {
    return this.allowMultipleFiles ? this.maxFileCount : 1;
  }

  // common method for processing files which are dropped or selected from file input dialog
  private process(filesToProcess: Array<File>): void {
    const results = processFiles({
      currentFiles: this.files,
      extensions: this.extensions,
      filesToProcess,
      maxFileCount: this.allowedMaxFileCount,
      maxFileSizeInBytes: this.maxFileSizeInBytes,
      maxFileSizeLabel: `${this.maxFileSizeValue} ${this.maxFileSizeUnit}`,
      allowDuplicates: this.allowDuplicates,
    });

    this.updateState(results);

    this.onFilesUploaded(results);
  }

  private updateState(results: Array<FileUploadResult> = []): void {
    const filesToUpload: Array<UploaderFile> = [];

    results.forEach(result => {
      if (result.uploaded) {
        filesToUpload.push(Object.assign(result.file, { id: result.id }));
      }
    });

    // handle multiple file setting
    if (this.allowMultipleFiles) {
      this.files = [...this.files, ...filesToUpload];
      return;
    }

    // handle single file setting
    if (filesToUpload.length) {
      const file = filesToUpload[0];
      this.files = [file];
    }
  }

  private onFilesUploaded(results: Array<FileUploadResult>): void {
    this.errors = [];

    const filesNotUploaded = results.filter(result => !result.uploaded);
    const filesUploaded = results.filter(result => result.uploaded);

    const event = new CustomEvent<FilesUploadedInfo>('on-files-uploaded', {
      detail: {
        allFiles: this.files,
        filesUploaded,
        filesNotUploaded,
      },
    });

    const uniqueErrors = filesNotUploaded
      ?.map(f => f.message)
      .filter((v, i, a) => a.indexOf(v) === i);
    this.errors = [...uniqueErrors];

    this.dispatchEvent(event);

    // Announce successful uploads on the polite live region so screen readers
    // are informed without requiring the user to move focus.
    if (filesUploaded.length > 0) {
      const names = filesUploaded.map(r => r.file.name).join(', ');
      const message =
        filesUploaded.length === 1
          ? `${names} uploaded successfully`
          : `${filesUploaded.length} files uploaded successfully: ${names}`;
      this.announce(message);
    }

    // When validation errors occur, move focus to the errors region so
    // keyboard users land on the relevant feedback. Screen readers will
    // still announce the messages via role="alert".
    if (this.errors.length > 0) {
      this.updateComplete.then(() => {
        const errorsEl = this.shadowRoot?.getElementById(
          'fileUploaderErrors'
        ) as HTMLElement | null;
        errorsEl?.focus();
      });
    }
  }

  /**
   * Push a message to one of the two alternating polite live regions.
   *
   * Screen readers, particularly NVDA, sometimes miss updates to a
   * live region when consecutive announcements share the same wording
   * or arrive within the same microtask. Toggling between two regions
   * (and only populating the newly-active one after a short delay)
   * ensures each announcement is treated as a distinct change and is
   * reliably picked up.
   */
  private announce(message: string): void {
    if (!message) return;

    if (this.statusAnnounceTimer) {
      clearTimeout(this.statusAnnounceTimer);
    }

    // Clear both regions first so any stale text does not interfere
    // with the upcoming announcement.
    this.statusMessage = '';
    this.statusMessageAlt = '';

    this.statusAnnounceTimer = setTimeout(() => {
      this.statusRegionToggle = !this.statusRegionToggle;
      if (this.statusRegionToggle) {
        this.statusMessageAlt = message;
      } else {
        this.statusMessage = message;
      }
    }, 100);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.statusAnnounceTimer) {
      clearTimeout(this.statusAnnounceTimer);
      this.statusAnnounceTimer = undefined;
    }
  }

  private onFileClick(e: CustomEvent<FileInfo>): void {
    const event = new CustomEvent<FileChangeInfo>('on-file-clicked', {
      detail: { allFiles: this.files, file: e.detail.file },
    });

    this.dispatchEvent(event);
  }

  private onFileDelete(e: CustomEvent<FileInfo>): void {
    this.pendingFileDeletion = e.detail.file;

    if (this.askConfirmationOnDeleteFile) {
      this.isDeleteBulkFileDialogOpen = true;
      return;
    }

    this.handleDeleteBulkFile();
  }

  handleDeleteBulkFile() {
    // Determine the sorted position of the file being removed so we can
    // move focus to the chip that takes its place (or the previous chip
    // when the removed chip was last).
    const sortedBeforeRemoval = sortFilesAscending([...this.files]);
    const removedIndex = sortedBeforeRemoval.findIndex(
      file => file.id === this.pendingFileDeletion?.id
    );
    const removedName = this.pendingFileDeletion?.name ?? '';

    this.files = [...this.files].filter(
      file => file.id !== this.pendingFileDeletion?.id
    );

    const event = new CustomEvent<FileChangeInfo>('on-file-removed', {
      detail: { allFiles: this.files, file: this.pendingFileDeletion },
    });

    this.isDeleteBulkFileDialogOpen = false;
    this.dispatchEvent(event);

    if (removedName) {
      this.announce(`${removedName} removed`);
    }

    // Move focus to a logical location after removal so keyboard users
    // are not stranded when the previously focused chip is unmounted.
    this.updateComplete.then(() => {
      const chips = Array.from(
        this.shadowRoot?.querySelectorAll('ix-file-chip') ?? []
      ) as Array<HTMLElement & { focusDelete?: () => void }>;

      if (chips.length > 0 && removedIndex >= 0) {
        const nextIndex = Math.min(removedIndex, chips.length - 1);
        const target = chips[nextIndex];
        if (target?.focusDelete) {
          target.focusDelete();
        } else {
          target?.focus?.();
        }
        return;
      }

      // No files remain: return focus to the upload input so the user can
      // easily try again.
      this.fileRef.value?.focus();
    });
  }

  public deleteLastFileWithoutConfirmation(): void {
    if (this.files.length === 0) {
      return; // no files to delete
    }

    this.pendingFileDeletion = this.files[this.files.length - 1];

    this.handleDeleteBulkFile();
  }

  private openFileUploadDialog(): void {
    this.fileRef.value?.click();
  }

  protected override firstUpdated(): void {
    this.files = [...this.defaultFiles];

    // connect the dropzone
    const dropzone = this.dropzoneRef.value;
    if (dropzone) {
      dropzone.addEventListener('dragenter', e => e.preventDefault());
      dropzone.addEventListener('dragover', e => e.preventDefault());
      dropzone.addEventListener('drop', this.onDrop.bind(this));
    }

    // connect the file input
    const fileElem = this.fileRef.value;
    if (fileElem) {
      fileElem.addEventListener('change', this.onFileChange.bind(this));
    }

    // calculate the maxBytes based on props
    // Calculate the max file size in bytes to use for checking if files are too large
    const totalBytesForStorageUnit = getTotalBytes(this.maxFileSizeUnit);
    this.maxFileSizeInBytes = this.maxFileSizeValue * totalBytesForStorageUnit;
  }

  protected update(changedProperties: PropertyValues) {
    super.update(changedProperties);

    if (changedProperties.has('defaultFiles')) {
      this.files = [
        ...this.files.filter(
          file => !this.defaultFiles.some(x => x.name === file.name)
        ),
        ...this.defaultFiles,
      ];
    }
  }

  isFileDeletable(file: UploaderFile): boolean {
    if (this.defaultFiles.some(x => x.name === file.name)) {
      return this.allowDeleteDefaultFiles;
    }

    return true;
  }

  private renderFileList(files: Array<UploaderFile> = []): TemplateResult<1> {
    const sortedFiles = sortFilesAscending(files);
    const total = sortedFiles.length;
    const chips = sortedFiles.map(
      (file: UploaderFile | undefined, index: number) => html`<ix-file-chip
        .file=${file}
        .deletable=${this.isFileDeletable(file!)}
        .position=${index + 1}
        .total=${total}
        @on-file-click=${this.onFileClick}
        @on-file-delete=${this.onFileDelete}
      ></ix-file-chip>`
    );

    const uploadedFileListClasses = {
      'uploaded-file-list': true,
      'uploaded-file-list--spaced': !this.hideBody,
    };

    const fileCount = files.length;
    const listLabel = `Uploaded files, ${fileCount} ${
      fileCount === 1 ? 'file' : 'files'
    }`;

    // Roles are applied via variables to reinstate the list semantics
    // that Safari's VoiceOver strips when `list-style: none` is used
    // (and to satisfy axe when the direct children of the <ul> are
    // custom elements rather than native <li> nodes). They are
    // intentionally not implicit — see comment in the styles file.
    const listRole = 'list';
    const listItemRole = 'listitem';

    return html`
      <ul
        class="${classMap(uploadedFileListClasses)}"
        role=${listRole}
        aria-label="${listLabel}"
      >
        ${chips}
        ${this.showTemplateFileLink
          ? html`<li
              class="ix-file-uploader__template-file"
              role=${listItemRole}
            >
              <slot name="template-file"></slot>
            </li>`
          : nothing}
      </ul>
      ${this.renderDeleteBulkFileConfirmationDialog()}
    `;
  }

  protected override updated(_: PropertyValues) {
    const formData = new FormData();

    this.files.forEach(file => {
      formData.append(`${this.name}[]`, file);
    });

    this.internals.setFormValue(formData);
  }

  renderDeleteBulkFileConfirmationDialog() {
    if (!this.askConfirmationOnDeleteFile) return nothing;

    const pendingName = this.pendingFileDeletion?.name ?? '';

    return html`<ix-dialog
      id="deleteFileConfirmationDialog"
      ?open=${this.isDeleteBulkFileDialogOpen}
      preventCloseOnScrimClick="true"
      role="alertdialog"
      aria-labelledby="delete-file-id"
      @closed=${() => {
        this.isDeleteBulkFileDialogOpen = false;
      }}
      @close=${() => {
        this.isDeleteBulkFileDialogOpen = false;
      }}
    >
      <form
        slot="content"
        id="delete-file-id"
        method="dialog"
        class="delete-file-confirmation-dialog"
      >
        ${this.deleteConfirmationDialogLabel}
      </form>
      <div slot="actions">
        <ix-button
          appearance="text"
          aria-label="Cancel deletion${pendingName ? ` of ${pendingName}` : ''}"
          @click=${() => {
            this.isDeleteBulkFileDialogOpen = false;
          }}
        >
          <span class="button-label">NO</span>
        </ix-button>
        <ix-button
          appearance="text"
          name="submitBtn"
          aria-label="Confirm deletion${pendingName
            ? ` of ${pendingName}`
            : ''}"
          @click=${() => this.handleDeleteBulkFile()}
        >
          <span class="button-label">YES</span>
        </ix-button>
      </div>
    </ix-dialog>`;
  }

  renderHintText() {
    if (!this.hintText) {
      return nothing;
    }

    return html`<div class="ix-file-uploader__hint-text" id="fileUploaderHint">
      ${this.hintText}
    </div>`;
  }

  renderErrors() {
    // Explicit list roles are used so Safari's VoiceOver does not strip
    // the semantics off the styled `<ul>` (`list-style: none`) and so
    // axe recognises the structure.
    const listRole = 'list';
    const listItemRole = 'listitem';

    return html`<div
      id="fileUploaderErrors"
      class="error-text"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      tabindex="-1"
    >
      ${this.errors.length
        ? html`<ul role=${listRole}>
            ${this.errors.map(
              e => html`<li role=${listItemRole}><span>* ${e}</span></li>`
            )}
          </ul>`
        : nothing}
    </div>`;
  }

  /**
   * Visually hidden alternating polite live regions used to announce
   * non-error status updates (successful uploads, removals) without
   * stealing focus from the user. Only one region contains text at a
   * time; alternating between them makes repeated announcements
   * detectable by screen readers.
   */
  renderStatusRegion() {
    return html`<div class="visually-hidden">
      <div
        id="fileUploaderStatus"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        ${this.statusMessage}
      </div>
      <div
        id="fileUploaderStatusAlt"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        ${this.statusMessageAlt}
      </div>
    </div>`;
  }

  renderBody() {
    const accept = this.extensions.map(ext => `.${ext}`).join(', ');

    const classes = {
      'ix-file-uploader--hide-body': this.hideBody,
    };

    const isInvalid = this.errors.length > 0 || this.error;
    const describedByIds = ['fileUploaderDesc', 'fileUploaderErrors'];
    if (this.hintText) describedByIds.push('fileUploaderHint');
    const describedBy = describedByIds.join(' ');

    // Only apply aria-label when we have a meaningful value; this lets a11y
    // audits still flag components that lack any accessible name.
    const inputLabel = this.ariaLabel || this.headerText || undefined;

    return html`<div class=${classMap(classes)}>
      <label class="dropzone" for="fileUploader" ${ref(this.dropzoneRef)}>
        <span class="ix-file-uploader__label">${this.headerText}</span>
        <span class="ix-file-uploader__help-text" id="fileUploaderDesc">
          ${this.bodyText}
        </span>
        <input
          id="fileUploader"
          accept=${accept}
          class="file-input"
          ?multiple=${this.allowMultipleFiles}
          onchange=${(e: Event) => this.onFileChange(e)}
          ${ref(this.fileRef)}
          aria-label="${ifDefined(inputLabel)}"
          aria-describedby="${describedBy}"
          aria-invalid="${isInvalid ? 'true' : 'false'}"
          type="file"
        />
      </label>
      ${this.renderErrors()} ${this.renderHintText()}
    </div>`;
  }

  protected render(): TemplateResult<1> {
    return html`<div
      class=${classMap({
        'ix-file-uploader': true,
        error: this.errors.length || this.error,
      })}
    >
      ${this.renderBody()}
      ${this.files.length ? this.renderFileList(this.files) : nothing}
      ${this.renderStatusRegion()}
    </div>`;
  }
}
